import React, { useEffect, useState } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import styled from '@emotion/styled';
import { FormHelperText } from '@mui/material';
import { selectUserDateFormat, selectUserTimeFormat } from '@/redux/selectors/authSelectors';
import { useAppSelector } from '@/redux/hooks';
import { rootElementId } from '@/constants/constants';
import PolarisIcon from '@/components/_common/PolarisIcon';

// https://www.npmjs.com/package/react-datepicker
// https://reactdatepicker.com/
// NOTE: CSS is imported manually per project because of the way it's bundled in the project

export type DatepickerProps = ReactDatePickerProps & {
  label?: string | null | undefined;
  onChange?(date: Date, event: React.SyntheticEvent<any> | undefined): void;
  showTimeSelect?: boolean;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  helperText?: string;
  isInsideModal?: boolean;
  bare?: boolean;
  advanceFilters?: boolean;
};

const Datepicker = ({
  selected,
  value,
  label,
  onChange,
  showTimeSelect = false,
  required = false,
  error = false,
  disabled = false,
  maxDate,
  className = '',
  helperText,
  isInsideModal,
  icon = <PolarisIcon icon="calendar" width={17} height={17} />,
  showIcon = true,
  calendarIconClassname,
  dateFormat,
  timeFormat,
  bare = false,
  advanceFilters = false,
  ...props
}: DatepickerProps) => {
  const userDateFormat = useAppSelector(selectUserDateFormat);
  const userTimeFormat = useAppSelector(selectUserTimeFormat);
  const [internalValue, setInternalValue] = useState<Date | null>(null);

  const handleChange = (val: Date, event: React.SyntheticEvent<any> | undefined) => {
    event?.stopPropagation();

    if (!event && showTimeSelect) {
      onChange(val, event);
      setInternalValue(val);
    } else if (event && showTimeSelect) {
      setInternalValue(val);
      onChange(val, event);
    } else {
      onChange(val, event);
    }
  };

  useEffect(() => {
    // this now allows clearing the selected value/datetime if we pass an `undefined` or `null` value to 'selected' OR 'value' props
    setInternalValue(selected || (value ? new Date(value) : null));
  }, [selected, value]);

  const popperContainer: DatepickerProps['popperContainer'] = ({ children }) => (
    <PopperContainer isInsideModal={isInsideModal}>{children}</PopperContainer>
  );

  const portalId = isInsideModal ? rootElementId : undefined;

  const isLabelVisible = advanceFilters || Boolean(internalValue && label);
  timeFormat = timeFormat ?? userTimeFormat;
  dateFormat = dateFormat ?? `${userDateFormat}${showTimeSelect ? ' ' + timeFormat : ''}`;

  const datepicker = (
    <DatePickerWrapped
      className="date-picker-wrapped"
      disabled={disabled}
      selected={internalValue}
      onChange={handleChange}
      showTimeSelect={showTimeSelect}
      showPopperArrow={false}
      timeFormat={timeFormat}
      dateFormat={dateFormat}
      placeholderText={`${label || ''} ${required ? '*' : ''}`}
      maxDate={maxDate}
      popperContainer={popperContainer}
      portalId={portalId}
      icon={icon}
      showIcon={showIcon}
      calendarIconClassname={`datepicker-icon${calendarIconClassname ? ' ' + calendarIconClassname : ''}`}
      advanceFilters={advanceFilters}
      {...props}
    />
  );

  if (bare) {
    return (
      <>
        {datepicker}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </>
    );
  }

  return (
    <>
      <DatePickerContainer
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        noLabel={!label}
        isLabelVisible={isLabelVisible}
        advanceFilters={advanceFilters}
        className={`${className}${error ? ' error' : ''}`}
      >
        {label && (
          <Label error={error} advanceFilters={advanceFilters}>
            {label}
            {required && <Asterisk className="asterisk"> *</Asterisk>}
          </Label>
        )}
        {datepicker}
      </DatePickerContainer>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </>
  );
};

export default Datepicker;

const Label = styled.label<{ error?: boolean; advanceFilters: boolean }>`
  max-width: 1000px;
  height: 11px;
  color: ${(props) => (props.error ? props.theme.palette.error.main : props.theme.palette.primary.main)};
  z-index: 9;
  display: inline-flex;
  align-items: center;
  font-size: 1.1rem;
  position: absolute;
  transform: translate(12px, 7px) scale(0.75);
  transform-origin: top left;
  opacity: 0;
  visibility: hidden;
  top: ${(props) => (props.advanceFilters ? props.theme.spacing(-1.5) : props.theme.spacing(0.5))};
  left: 0;
  background: ${(props) => (props.advanceFilters ? props.theme.palette.background.paper : 'transparent')};
  padding: ${(props) => (props.advanceFilters ? props.theme.spacing(0, 0.5, 0, 0.1) : props.theme.spacing(0))};
`;

const datePickerCursor = 'pointer';

const DatePickerWrapped = styled(ReactDatePicker)<{ advanceFilters: boolean }>`
  cursor: ${datePickerCursor};
  color: ${(props) => props.theme.palette.text.primary};
  background: transparent;
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: 1rem;
  font-weight: ${(props) => props.theme.typography.fontWeightNormal};
  border: 0;
  display: block;
  width: 100%;

  &::placeholder {
    line-height: ${(props) => props.theme.typography.body2?.lineHeight};
    font-size: ${(props) => props.theme.typography.body1?.fontSize};
    font-weight: ${(props) => props.theme.typography.body1?.fontWeight};
    color: ${(props) => props.theme.palette.text.secondary};
    margin-top: ${(props) => (props.advanceFilters ? props.theme.spacing(-1) : props.theme.spacing(0))};
  }

  &:focus-visible {
    outline: 0;
  }
`;

const DatePickerContainer = styled.div<{
  disabled?: boolean;
  noLabel?: boolean;
  hasValue?: boolean;
  isLabelVisible?: boolean;
  advanceFilters: boolean;
}>`
  position: relative;
  border-radius: ${(props) => (props.advanceFilters ? 4 : props.theme.shape.borderRadius * 0.75)}px;
  border: ${(props) =>
    props.advanceFilters
      ? `1px solid ${props.theme.palette?.filters?.input?.border || (props.theme.palette.mode === 'dark' ? '#313339' : '#a4a4a4')}`
      : 0};
  box-sizing: border-box;
  padding: ${(props) => props.theme.spacing(1.0842, 1)};
  margin-right: 0;
  background-color: ${(props) =>
    props.advanceFilters
      ? props.theme.palette?.filters?.input?.background || (props.theme.palette.mode === 'dark' ? '#25272C' : '#ffffff')
      : props.theme.palette.mode === 'dark'
        ? 'rgba(171, 171, 220, 0.15)'
        : 'rgba(0, 0, 0, 0.09)'};
  transition: background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;

  & .date-picker-wrapped {
    padding: ${(props) => props.theme.spacing(1.05, 0.5)};
  }

  & .react-datepicker__calendar-icon {
    // reset width and height of the icon container with allow icon to be of a size of icon
    width: initial;
    height: initial;
  }

  .datepicker-icon {
    cursor: ${datePickerCursor};
    pointer-events: none; // this will all to click through the icon, so the input tag will still be clickable
    right: 0;
    top: ${(props) => (props.advanceFilters ? '60%' : '50%')};
    transform: translateY(-50%);
  }

  &.error {
    border-color: ${(props) => props.theme.palette.error.main};

    & label,
    & .asterisk,
    & .date-picker-wrapped::placeholder {
      color: ${(props) => props.theme.palette.error.main};
      padding: 0;
    }
  }

  &:hover,
  &:focus {
    background-color: ${(props) =>
      props.advanceFilters
        ? props.theme.palette?.filters?.input?.focused || (props.theme.mode === 'dark' ? '#313339' : '#ffffff')
        : props.theme.palette.mode === 'dark'
          ? 'rgba(171, 171, 220, 0.18)'
          : 'rgba(0, 0, 0, 0.13)'};
    transition: ${(props) => (props.advanceFilters ? 'border-color' : 'background-color')} 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    border-style: solid;
    border-color: ${(props) => (props.advanceFilters ? props.theme.palette.grey['500'] : props.theme.palette.primary.light)};
  }

  & .react-datepicker-wrapper {
    display: flex;
    align-items: center;
  }

  ${(props) =>
    props.disabled
      ? `
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
  `
      : null};

  ${(props) =>
    props.noLabel
      ? `
      & legend {
        display: none;
        padding: 0;
      }
  `
      : null};

  ${(props) =>
    props.isLabelVisible
      ? `
      .date-picker-wrapped {
        padding: ${props.theme.spacing(props.advanceFilters ? 1.5 : 2.1, 0.5, props.advanceFilters ? 0.75 : 0)};
      }

      label {
        opacity: 1;
        visibility: visible;
      }
  `
      : null};
`;

const PopperContainer = styled.div<{ isInsideModal?: boolean }>`
  ${(props) =>
    props.isInsideModal
      ? `
      position: relative;
      z-index: ${props.theme.zIndex.modal + 1};
      `
      : ''}

  & .react-datepicker-popper {
    z-index: 10;

    &[data-placement^='bottom'] {
      padding-top: ${(props) => props.theme.spacing(1)};
    }
  }

  & .react-datepicker {
    font-family: ${(props) => props.theme.typography.fontFamily};
    border-radius: ${(props) => props.theme.shape.borderRadius};
    border-color: ${(props) => props.theme.palette.primary.light + '6a'};
    display: flex;
    background-color: ${(props) => props.theme.palette.background.paper};
    font-size: 1rem;
  }

  & .react-datepicker__time-container {
    border-color: ${(props) => props.theme.palette.action.disabledBackground};
  }

  & .react-datepicker__time-container .react-datepicker__time {
    background: none;

    & .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
      height: 20px;
      display: flex;
      justify-content: center;
      align-items: center;

      &--selected {
        background: ${(props) => props.theme.palette.primary.main};
      }

      &:hover {
        background: ${(props) => props.theme.palette.primary.main + '6a'};
      }
    }
  }

  & .react-datepicker__month {
    padding: ${(props) => props.theme.spacing(1)};
  }

  & .react-datepicker-year-header,
  & .react-datepicker__day,
  & .react-datepicker__month-text,
  & .react-datepicker__quarter-text,
  & .react-datepicker__year-text {
    color: ${(props) => props.theme.palette.text.primary};
    &:hover {
      background: ${(props) => props.theme.palette.primary.main + '6a'};
      color: ${(props) => props.theme.palette.text.primary};
    }
  }

  & .react-datepicker__current-month,
  & .react-datepicker-time__header {
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 1rem;
  }

  & .react-datepicker__day--today,
  & .react-datepicker__month-text--today,
  & .react-datepicker__quarter-text--today,
  & .react-datepicker__year-text--today {
    background: ${(props) => props.theme.palette.primary.dark + '3a'};
    border-radius: 0.3rem;
  }

  & .react-datepicker__day-names {
    margin-top: ${(props) => props.theme.spacing(2)};
  }

  & .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list,
  & .react-datepicker__day-name {
    color: ${(props) => props.theme.palette.text.primary};
  }

  & .react-datepicker__navigation {
    top: ${(props) => props.theme.spacing(1)};
  }

  & .react-datepicker__year-read-view--down-arrow,
  & .react-datepicker__month-read-view--down-arrow,
  & .react-datepicker__month-year-read-view--down-arrow,
  & .react-datepicker__navigation-icon::before {
    border-width: 1px 1px 0 0;
    height: 7px;
    width: 7px;
  }

  & .react-datepicker__header {
    background: none;
    border: 0;
    padding-top: ${(props) => props.theme.spacing(1.5)};
  }

  & .react-datepicker__header--time {
    padding-right: ${(props) => props.theme.spacing(1.5)};
  }

  & .react-datepicker__day-name {
    opacity: 0.5;
    font-weight: ${(props) => props.theme.typography.fontWeightLight};
  }

  & .react-datepicker__day--selected,
  & .react-datepicker__day--keyboard-selected {
    color: ${(props) => props.theme.palette.text.primary};

    &:hover {
      background: ${(props) => props.theme.palette.primary.main};
    }
  }

  & .react-datepicker__day--selected {
    background: ${(props) => props.theme.palette.primary.main};
  }

  & .react-datepicker__day--keyboard-selected {
    background: ${(props) => `${props.theme.palette.primary.main}77`};
  }

  & .react-datepicker__day--disabled,
  & .react-datepicker__time-list-item--disabled {
    opacity: 0.2;
  }
`;

const Asterisk = styled.span`
  transform: scale(1.5);
  margin-left: ${(props) => props.theme.spacing(1)};
  margin-right: ${(props) => props.theme.spacing(-0.4)};
  color: ${(props) => props.theme.palette.primary.main};
`;
